import { authorizedApiClient } from "./config";

export type AccountCreationThreshold = "first_message" | "two_way";

export type EmailSettings = {
  id: string;
  workspace_id: string;
  create_from_email: boolean;
  classifier_instructions: string | null;
  domain_disallow_list: string[];
  domain_allow_list: string[];
  internal_domains: string[];
  account_creation_threshold: AccountCreationThreshold | string;
  default_subscription_status?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type EmailDomainReviewStatus = "pending" | "allowed" | "blocked";

export type EmailDomainReviewVerdict = "ambiguous" | "not_customer";

export type EmailDomainReviewItem = {
  id: string;
  domain: string;
  first_seen: string | null;
  last_seen: string | null;
  msg_count: number;
  verdict: EmailDomainReviewVerdict | string;
  reason: string | null;
  status: EmailDomainReviewStatus | string;
  reviewed_at: string | null;
};

export type EmailDomainReviewList = {
  items: EmailDomainReviewItem[];
  total: number;
  pending_count: number;
  sensitive_excluded_count?: number;
};

export const getEmailSettings = async ({
  workspace_id,
}: { workspace_id: string }) => authorizedApiClient.get(`/${workspace_id}/email-settings`);

export type UpdateEmailSettings = {
  workspace_id: string;
  create_from_email?: boolean;
  classifier_instructions?: string | null;
  domain_disallow_list?: string[];
  domain_allow_list?: string[];
  internal_domains?: string[];
  account_creation_threshold?: AccountCreationThreshold | string;
  default_subscription_status?: string | null;
};

export const updateEmailSettings = async ({
  workspace_id,
  create_from_email,
  classifier_instructions,
  domain_disallow_list,
  domain_allow_list,
  internal_domains,
  account_creation_threshold,
  default_subscription_status,
}: UpdateEmailSettings) => {
  const payload: any = {};
  if (create_from_email !== undefined) payload.create_from_email = create_from_email;
  if (classifier_instructions !== undefined) payload.classifier_instructions = classifier_instructions;
  if (domain_disallow_list !== undefined) payload.domain_disallow_list = domain_disallow_list;
  if (domain_allow_list !== undefined) payload.domain_allow_list = domain_allow_list;
  if (internal_domains !== undefined) payload.internal_domains = internal_domains;
  if (account_creation_threshold !== undefined) payload.account_creation_threshold = account_creation_threshold;
  if (default_subscription_status !== undefined) payload.default_subscription_status = default_subscription_status;

  return authorizedApiClient.patch(`/${workspace_id}/email-settings`, payload);
};

export const getEmailDomainReview = async ({
  workspace_id,
  status,
}: { workspace_id: string; status?: EmailDomainReviewStatus }) => authorizedApiClient.get(
  `/${workspace_id}/email-domain-review`,
  { params: status ? { status } : undefined },
);

export const allowEmailDomain = async ({
  workspace_id,
  domain,
  note,
}: { workspace_id: string; domain: string; note?: string }) => authorizedApiClient.post(
  `/${workspace_id}/email-domain-review/${encodeURIComponent(domain)}/allow`,
  note !== undefined ? { note } : {},
);

export const blockEmailDomain = async ({
  workspace_id,
  domain,
  note,
}: { workspace_id: string; domain: string; note?: string }) => authorizedApiClient.post(
  `/${workspace_id}/email-domain-review/${encodeURIComponent(domain)}/block`,
  note !== undefined ? { note } : {},
);
